import { ApiResult, HttpClient } from "../http.js";
import {
  NotificationDto,
  NotificationPreferencesDto,
} from "../types/notifications.js";

export interface ListNotificationsQuery {
  /** Newest-first cap (1–100). Defaults to 20 server-side when omitted. */
  maxCount?: number;
}

export interface MarkAllNotificationsReadPayload {
  ids: string[];
}

export interface DeleteCreditorInvoiceNotificationsPayload {
  creditorInvoiceId: string;
}

export interface RegisterPushTokenPayload {
  /** The Expo push token. Globally unique — re-registering re-points the existing row. */
  token: string;
  platform: "ios" | "android" | "web";
  deviceName?: string | null;
}

export interface UnregisterPushTokenPayload {
  token: string;
}

export class NotificationsApi {
  constructor(private readonly http: HttpClient) {}

  /** Lists the current user's notifications (active-org + cross-org partner buckets). */
  list(query: ListNotificationsQuery = {}): Promise<ApiResult<NotificationDto[]>> {
    return this.http.get<NotificationDto[]>("/api/notifications", {
      query: query.maxCount === undefined ? {} : { maxCount: query.maxCount },
    });
  }

  /** Count of the current user's unread notifications across both buckets. */
  unreadCount(): Promise<ApiResult<number>> {
    return this.http.get<number>("/api/notifications/unread-count");
  }

  /** Marks a single notification read (idempotent). Returns the updated row. */
  markRead(id: string): Promise<ApiResult<NotificationDto>> {
    return this.http.post<NotificationDto>(`/api/notifications/${encodeURIComponent(id)}/read`);
  }

  /** Marks the supplied notification ids read. Returns the number of rows newly flipped. */
  markAllRead(payload: MarkAllNotificationsReadPayload): Promise<ApiResult<number>> {
    return this.http.post<number>("/api/notifications/read-all", { body: payload });
  }

  /** Deletes the current user's creditor-invoice notifications for one invoice id. */
  deleteForCreditorInvoice(
    payload: DeleteCreditorInvoiceNotificationsPayload,
  ): Promise<ApiResult<number>> {
    return this.http.post<number>("/api/notifications/delete-creditor-invoice", { body: payload });
  }

  /**
   * Registers this device's push token for the signed-in user. Idempotent: the app
   * calls it on every login and an already-known token is re-pointed, not duplicated.
   */
  registerPushToken(payload: RegisterPushTokenPayload): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>("/api/notifications/push-tokens", { body: payload });
  }

  /** Removes a device's push token on logout. Idempotent — an unknown token is a no-op. */
  unregisterPushToken(payload: UnregisterPushTokenPayload): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>("/api/notifications/push-tokens/unregister", { body: payload });
  }

  /**
   * The signed-in user's own notification preferences. Never 404s — a user who has
   * never saved gets the server-side defaults.
   */
  getPreferences(): Promise<ApiResult<NotificationPreferencesDto>> {
    return this.http.get<NotificationPreferencesDto>("/api/notifications/preferences");
  }

  /** Replaces the signed-in user's notification preferences. Returns the stored result. */
  updatePreferences(
    payload: NotificationPreferencesDto,
  ): Promise<ApiResult<NotificationPreferencesDto>> {
    return this.http.put<NotificationPreferencesDto>("/api/notifications/preferences", {
      body: payload,
    });
  }
}
